import { get, isEmpty, set } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Alert } from 'antd';
import useTimer from '../hooks/useTimer';
import useUpdatingKioskState from '../hooks/useUpdatingKioskState';
import useInactivityLogout from '../hooks/useInactivityLogout';
import kiosks from '../resources/kiosks';
import { CREDENTIALS, getLoginType, logout } from '../resources/auth';
import { isMobileDevice, showLogoutButton } from '../utils';
import kioskUtils from '../utils/kiosk';
import productUtils, { DURATION_POLICY } from '../utils/product';
import ClosedKiosk from '../components/ClosedKiosk';
import ErrorContainer from '../components/ErrorContainer';
import ButtonContainer from '../components/ButtonContainer';
import { Button, Form, Modal } from '../components';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useForm } from 'antd/es/form/Form';
import ValidityForm from './ValidityForm';
import CustomerForm from './CustomerForm';
import ProductInfo from '../components/ProductInfo';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

const PLATE_QUOTA_ERROR_CODE = 'KIOSK_PLATE_QUOTA_EXCEEDED';

const FlexContainer = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column wrap;
  gap: 0 20px;

  @media screen and (hover: none) and (min-device-aspect-ratio: 1/1) and (orientation: landscape) {
    flex-direction: row;
  }
`;

const RegistrationLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 20px;
  margin-top: 20px;
  width: 100%;
`;

const ProductInfoColumn = styled.div`
  flex: 0 1 300px;
  min-width: min(300px, 100%);
`;

const FormSection = styled.div`
  flex: 1 1 320px;
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
`;

const FormContainer = styled(Form)`
  width: 100%;
  max-width: min(900px, 100%);

  @media screen and (max-width: 600px) {
    max-width: 100%;
  }
`;

const RegistrationActions = styled.div`
  width: 100%;
  max-width: min(900px, 100%);
`;

const ContinueOrRegisterButton = ({ onContinue, onRegister, hasNextStep, disabled }) => {
  const { t } = useTranslation();

  if (hasNextStep) {
    return <Button type="primary" className="btn-continue" onClick={onContinue}>
      {t('continue')} <ArrowRightOutlined style={{ flex: 0 }}/>
    </Button>;
  }

  return <Button
    type="primary"
    className="btn-register"
    disabled={disabled}
    onClick={onRegister}
  >
    {t('register')}
  </Button>;
};

const BackOrLogoutButton = ({ kioskState, onClose, currentStep, setStep }) => {
  const { t } = useTranslation();
  const visibleProducts = kioskUtils.getVisibleProducts(kioskState?.kiosk);

  if (currentStep > 0) {
    return (<>
      <Button className="backButton" onClick={() => setStep(0)}>
        <ArrowLeftOutlined/> {t('back')}
      </Button>
    </>);
  }

  // Back to product selection
  if (visibleProducts?.length > 1) {
    return <Button disabled={!kioskState.connectionOk} className="backButton" onClick={() => onClose()}>
      <ArrowLeftOutlined/> {t('back')}
    </Button>;
  }

  if (showLogoutButton()) {
    return <Button disabled={!kioskState.connectionOk} className="backButton" onClick={logout}>
      {t('logout')}
    </Button>;
  }
  return null;
};

const getCompletePhoneNumber = (phoneNumber, phoneCode) => {
  if (!phoneNumber) {
    return null;
  }
  // Remove first zero and add phone code
  return phoneCode.concat(phoneNumber.replace(/^0*/, ''));
};

const mapToKioskPermit = (data, phoneCode) => {
  const fields = {};

  const { validFrom, validTo, licensePlate, email, phoneNumber } = data;

  if (validFrom) {
    set(fields, 'validity.validFrom', validFrom);
  }
  if (validTo) {
    set(fields, 'validity.validTo', validTo);
  }
  if (licensePlate) {
    fields.licensePlates = [ licensePlate ];
  }
  if (email) {
    set(fields, 'customer.email', email);
  }
  if (phoneNumber) {
    set(fields, 'customer.phoneNumber', getCompletePhoneNumber(phoneNumber, phoneCode));
  }
  return fields;
};

const RegistrationForm = ({ location: { state }, history }) => {
  const { t } = useTranslation();
  const [ form ] = useForm();

  const { selectedProductId, unlockCode } = state;
  const [ kioskState ] = useUpdatingKioskState();
  const kiosk = kioskState?.kiosk;

  if (!kiosk) {
    history.replace('/login');
  }

  const selectedProduct = kiosk?.kioskProducts?.find(prod => prod.uid === selectedProductId);
  const countryCode = kiosk?.countryCode;

  const returnToLocationSelection = () => {
    history.replace('/parking-locations');
  };

  const [ phoneCode, setPhoneCode ] = useState();

  const [ isDirty, setIsDirty ] = useState(false);
  const [ registering, setRegistering ] = useState(false);
  const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
  const [ generalError, setGeneralError ] = useState('');
  const [ quotaModalOpen, setQuotaModalOpen ] = useState(false);
  const [ isAvailable, setIsAvailable ] = useState(true);
  const [ step, setStep ] = useState(0);
  const resetTimer = useTimer(returnToLocationSelection, true);
  const resetLogoutTimer = useInactivityLogout(!isMobileDevice() && getLoginType() === CREDENTIALS);
  const isFlexiProduct = selectedProduct?.durationPolicy === DURATION_POLICY.SELECTABLE_END;
  const initialDateTime = productUtils.getFirstAvailableStartDate(kiosk, selectedProduct);
  const initialValues = { validFrom: initialDateTime, validTo: initialDateTime };

  useEffect(() => {
    setIsAvailable(kioskUtils.isKioskAvailable(kiosk));
  }, [ kioskState ]);

  if (!state.selectedProductId || !selectedProduct || !kiosk) {
    returnToLocationSelection();
    return null; // Return null to stop rendering the view
  }

  if (!isAvailable) {
    return <FlexContainer>
      <ErrorContainer><ClosedKiosk kiosk={kiosk}/></ErrorContainer>
    </FlexContainer>;
  }

  const registerPlate = async () => {
    setRegistering(true);

    if (kiosk?.warnLicensePlateWithoutOngoingSessions) {
      await form.validateFields().finally(() => setRegistering(false));
      kiosks.checkOngoingSessions(selectedProductId, form.getFieldValue('licensePlate'))
        .then(response => {
          if (!response.hasOngoingSessions) {
            setConfirmModalOpen(true);
          } else {
            applyChanges();
          }
        });
    } else {
      applyChanges();
    }
  };

  const applyChanges = async () => {
    try {
      const data = await form.validateFields();
      const payload = {
        ...mapToKioskPermit(data, phoneCode),
        'kioskProductId': selectedProductId,
        unlockCode,
      };
      createPermit(payload).catch(() => setGeneralError(t('error.registerGeneralError')));
    } catch (error) {
      // Don't do anything with validation errors
    } finally {
      setRegistering(false);
    }
  };

  const createPermit = async data => await kiosks.createPermit(data)
    .then(permit => history.push('/success', { permit }))
    .catch(e => {
      const errorMessage = get(e, 'response.data.message', '{}');
      if (errorMessage.includes(PLATE_QUOTA_ERROR_CODE)) {
        setQuotaModalOpen(true);
        return;
      }
      try {
        const error = JSON.parse(errorMessage).message === 'No availability to create permit' ?
          t('error.permitAvailability') : t('error.registerGeneralError');
        setGeneralError(error);
      } catch (e) {
        setGeneralError((t('error.registerGeneralError')));
      }
    });
  const handleChange = field => {
    setIsDirty(true);
    resetLogoutTimer();
    resetTimer();
  };
  return (
    <FlexContainer>
      <>
        <RegistrationLayout>
          <ProductInfoColumn>
            <ProductInfo product={selectedProduct}/>
          </ProductInfoColumn>
          <FormSection>
            <FormContainer
              layout="vertical"
              form={form}
              initialValues={initialValues}
              onValuesChange={handleChange}
              requiredMark={false}
            >
              <CustomerForm
                kiosk={kiosk}
                phoneCode={phoneCode}
                setPhoneCode={setPhoneCode}
                onChange={handleChange}
                hidden={step > 0}
              />
              <ValidityForm
                countryCode={countryCode}
                product={selectedProduct}
                initialDateTime={initialDateTime}
                onChange={handleChange}
                hidden={step === 0}
                kiosk={kiosk}
              />
            </FormContainer>
            <RegistrationActions>
              {generalError && <Alert type="error" message={generalError}/>}
              <ButtonContainer style={{ flex: 0, paddingTop: 8, alignItems: 'flex-start' }}>
                <ContinueOrRegisterButton
                  onRegister={registerPlate}
                  onContinue={() => form.validateFields()
                    .then(() => setStep(step + 1))}
                  hasNextStep={isFlexiProduct && step === 0}
                  disabled={!isDirty || registering || !kioskState.connectionOk}
                />
                <BackOrLogoutButton
                  currentStep={step}
                  setStep={setStep}
                  kioskState={kioskState}
                  onClose={returnToLocationSelection}
                />
              </ButtonContainer>
            </RegistrationActions>
          </FormSection>
        </RegistrationLayout>


        <Modal
          title={t('warnLicensePlateWithoutOngoingSessions.title')}
          visible={confirmModalOpen}
          centered={true}
          width="700px"
          closeIcon={null}
          footer={[
            <Button disabled={!kioskState.connectionOk} key="continue" onClick={() => {
              setConfirmModalOpen(false);
              applyChanges();
            }}>
              {t('continue')}
            </Button>,
            <Button key="back" type="primary" onClick={() => {
              setConfirmModalOpen(false);
              setRegistering(false);
            }}>
              {t('back')}
            </Button>,
          ]}>
          {t('warnLicensePlateWithoutOngoingSessions.body')}
        </Modal>

        <Modal
          title={t('error.quotaReachedTitle')}
          visible={quotaModalOpen}
          centered={true}
          width="700px"
          closeIcon={null}
          footer={[
            <Button key="ok" type="primary" onClick={() => setQuotaModalOpen(false)}>
              {t('close')}
            </Button>,
          ]}>
          {t('error.quotaReached')}
        </Modal>
      </>
    </FlexContainer>
  );
};

export default RegistrationForm;
